import React, { useState, useRef, useEffect } from "react";
import * as requests from "util/requests";
import { TextField, Checkbox } from "@mui/material";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import ElementTitle from "ui/crfs/element_title";
import DividerLine from "ui/crfs/divider_line";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import styles from "./date.module.css";
import moment from "moment";
import DataCompleteChangeModal from "ui/crfs/data_complete_change_modal";
import EntryRuleOverrideModal from "ui/crfs/entry_rule_override_modal";
import AuditIconUI from "ui/crfs/audit_icon_ui";
import ElementMenu from "ui/crfs/element_menu";
import LoadingButton from "@mui/lab/LoadingButton";

let tempStorage = null;

export default function DateUI(props) {
  const {
    field,
    title,
    onUpdate,
    crf_id,
    crfStatus,
    read_only,
    first_randomized_on,
    captureCompleteDateChangeReason = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  //we must set localVal anytime the UI changes to keep it in sync, the DatePicker doesn't seem to like being at a different state than localVal, perhaps it becomes uncontrolled
  const [localVal, setLocalVal] = useState(field.value_obj.value || null);
  const [dataChange, setDataChange] = useState(false);
  const [showingCompleteChangeModal, setShowingCompleteChangeModal] = useState(false);
  const [showingEntryOverrideModal, setShowingEntryOverrideModal] = useState(false);

  //useEffect to set the tempStorage incase we just use the text entry
  useEffect(() => {
    tempStorage = field.value_obj.value;
    setLocalVal(field.value_obj.value);
  }, [crf_id, field]);

  const commonUpdateValueActions = (value, reason = null) => {
    if (value === tempStorage) {
      return;
    }
    let obj = {
      data_id: field.value_obj?.data_id || null,
      flags: field.value_obj?.flags || null,
      value,
      value_name: field.value_name || null,
      reason,
    };
    onUpdate(obj);
    tempStorage = value;
  };

  const considerUpdate = (value = undefined) => {
    if (crfStatus === "data_complete" && captureCompleteDateChangeReason) {
      setShowingCompleteChangeModal(true);
      return;
    }
    if (value === null || value) {
      commonUpdateValueActions(value);
    } else {
      commonUpdateValueActions(localVal);
    }
  };

  const acceptTempStorage = (reason) => {
    commonUpdateValueActions(localVal, reason);
    tempStorage = localVal;
    setShowingCompleteChangeModal(false);
    setShowingEntryOverrideModal(false);
    setDataChange(false);
  };

  const closeModal = () => {
    setShowingCompleteChangeModal(false);
    setShowingEntryOverrideModal(false);
    setLocalVal(tempStorage);
    setDataChange(false);
  };

  const openDateUI = () => {
    // tempStorage = localVal;
    setIsOpen(true);
  };
  const closeDateUI = () => {
    setIsOpen(false);
    // considerUpdate();
  };

  const handleOnChange = (value) => {
    if (value && value.isValid()) {
      if (!entry_rule_error(value.format("yyyy-MM-DD"))) {
        considerUpdate(value.format("yyyy-MM-DD"));
      }
      setLocalVal(value.format("yyyy-MM-DD"));
      setDataChange(true);
    } else if (value === null) {
      setLocalVal(null);
      considerUpdate(null);
      setDataChange(true);
    }
  };

  const updateValue = (error_override = false) => {
    if (crfStatus === "data_complete" && captureCompleteDateChangeReason) {
      setShowingCompleteChangeModal(true);
      return;
    } else if (entry_rule_error() && !error_override) {
      if (field.entry_rule_override) {
        setShowingEntryOverrideModal(true);
      } else {
        setLocalVal(tempStorage);
        setDataChange(false);
      }
      return;
    } else {
      commonUpdateValueActions(localVal);
      setDataChange(false);
    }
  };

  const onBlur = (e) => {
    if (e.target.value) updateValue();
  };

  const entry_rule_error = (value = null) => {
    let _value = value || localVal;
    let error = false;
    if (_value?.length > 4 && field.entry_rules && (value || dataChange)) {
      let _date = _value.slice(0, 4);
      field?.entry_rules.some((rule) => {
        if (rule.rule_type === "min") {
          if (moment(_value).diff(moment(rule.value)) < 0) {
            error = `Must be greater than or equal to ${rule.value}`;
            return error;
          }
        } else if (rule.rule_type === "max") {
          if (moment(_value).diff(moment(rule.value)) > 0) {
            error = `Must be less than or equal to ${rule.value}`;
            return error;
          }
        } else if (rule.rule_type === "min_age") {
          //careful with Date here when we call the main function Date as well (renamed)

          if (moment().diff(moment(_value)) < rule.value * 365 * 24 * 60 * 60 * 1000) {
            error = `Age must be greater than ${rule.value}`;
            return error;
          }
        } else if (rule.rule_type === "max_age") {
          if (moment().diff(moment(_value)) > rule.value * 365 * 24 * 60 * 60 * 1000) {
            error = `Age must be less than ${rule.value}`;
            return error;
          }
        } else if (rule.rule_type === "not_future") {
          let current_year = new Date().getFullYear();
          if (current_year < parseInt(_date)) {
            error = `Must be the current year or in the past.`;
            return error;
          }
        } else if (rule.rule_type === "not_past") {
          let current_year = new Date().getFullYear();
          if (current_year > parseInt(_date)) {
            error = `Must be the current year or in the future.`;
            return error;
          }
        } else if (rule.rule_type === "after_randomization" && first_randomized_on) {
          if (moment(_value).diff(moment(moment.utc(first_randomized_on)).tz(moment.tz.guess()), "days") < 0) {
            error = `Must be greater than or equal to ${moment.utc(first_randomized_on).tz(moment.tz.guess()).format("yyyy-MM-DD")}`;
            return error;
          }
        } else if (rule.rule_type === "before_randomization" && first_randomized_on) {
          if (moment(_value).diff(moment(moment.utc(first_randomized_on)).tz(moment.tz.guess()), "days") > 0) {
            error = `Must be less than or equal to ${moment.utc(first_randomized_on).tz(moment.tz.guess()).format("yyyy-MM-DD")}`;
            return error;
          }
        }
      });
    }
    return error;
  };

  return (
    <div className={styles.outer_wrapper}>
      <div className={styles.input_flex}>
        <div className={styles.left_split}>
          <ElementTitle required={field.required} title={title} />
        </div>
        {read_only || field.read_only ? (
          <div className={styles.right_split}>
            <div className={styles.flex_row}>
              <div>{localVal || "--"}</div>
              <div className={styles.audit_icon}>
                <ElementMenu {...props} />
              </div>
            </div>
            <div>{field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}</div>
          </div>
        ) : (
          <div className={styles.right_split}>
            <div className={styles.flex_column}>
              <div className={styles.flex_row}>
                <div className={styles.date_wrapper}>
                  <div>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        open={isOpen}
                        format="YYYY / MM / DD"
                        value={(localVal && moment(localVal, "yyyy-MM-DD")) || null}
                        sx={{
                          "& .MuiInputBase-input": {
                            padding: "5px 10px",
                            width: "120px",
                          },
                          "& .MuiInputAdornment-root": { marginLeft: 0 },
                          "& .MuiButtonBase-root": { paddingLeft: 0 },
                        }}
                        onChange={handleOnChange}
                        onOpen={openDateUI}
                        onClose={closeDateUI}
                        slotProps={{
                          textField: { onBlur },
                          actionBar: {
                            actions: ["clear"],
                          },
                        }}
                        disableFuture={!!field?.entry_rules?.find((r) => r.rule_type === "not_future") || false}
                        disablePast={!!field?.entry_rules?.find((r) => r.rule_type === "not_past") || false}
                      />
                    </LocalizationProvider>
                  </div>
                  <div className={styles.input_description}>(YYYY / MM / DD)</div>
                </div>

                <div className={styles.audit_icon}>
                  <ElementMenu {...props} />
                </div>
              </div>
              {field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}
              <div className={styles.flex_row}>
                {entry_rule_error().constructor === String ? (
                  <div className={styles.input_flex}>
                    <div className={styles.error}>{entry_rule_error()}</div>{" "}
                    {field?.entry_rule_override ? (
                      <LoadingButton
                        variant="outlined"
                        loading={false}
                        onClick={() => updateValue()}
                        sx={{
                          fontSize: 14,
                          padding: "2px",
                          paddingLeft: "10px",
                          paddingRight: "10px",
                          marginLeft: "10px",
                        }}
                      >
                        Save Anyway
                      </LoadingButton>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      <DividerLine />
      <DataCompleteChangeModal isOpen={showingCompleteChangeModal} close={closeModal} onYes={acceptTempStorage} />
      <EntryRuleOverrideModal isOpen={showingEntryOverrideModal} close={closeModal} onYes={acceptTempStorage} />
    </div>
  );
}
